// The pages of a receipt: a strip of thumbnails with an "Add pages" tile (drop, paste or choose files, several at once),
// a remove button on each page, and a viewer that steps through them. One strip serves the receipt screen (changes
// happen at once), Add a purchase (pages are files not saved yet) and Edit receipt (both, applied on Save).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DeleteIcon, UploadIcon, XSmallIcon } from '@shopify/polaris-icons';
import { Ic } from '../ui';
import { itApi } from './apiClient';
import { Alert, Modal, useIt } from './common';
import { acceptedFiles, isImageFile, pagesText, pastedFiles } from './pages';

export const MAX_PAGES = 20;

// Object URLs for pages that are still Files (not uploaded yet): { key: { url, isImage } }.
export function useNewPages(files) {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    const next = {};
    files.forEach((f) => { next[f.key] = { url: isImageFile(f.file) ? URL.createObjectURL(f.file) : null, isImage: isImageFile(f.file) }; });
    setUrls(next);
    return () => Object.values(next).forEach((u) => { if (u.url) URL.revokeObjectURL(u.url); });
  }, [files]);
  return urls;
}

// The stored pages of a receipt fetched for their thumbnails: { fileId: { url, type } | 'failed' }.
export function useStoredPages(receiptId, files) {
  const [blobs, setBlobs] = useState({});
  const ids = (files || []).filter((f) => f.kind === 'upload').map((f) => f.id).join(',');
  useEffect(() => {
    let live = true; const urls = [];
    setBlobs({});
    ids.split(',').filter(Boolean).forEach((id) => {
      itApi.receiptPage(receiptId, Number(id)).then((b) => {
        if (!live) return;
        const url = URL.createObjectURL(b); urls.push(url);
        setBlobs((prev) => ({ ...prev, [id]: { url, type: b.type || '' } }));
      }, () => { if (live) setBlobs((prev) => ({ ...prev, [id]: 'failed' })); });
    });
    return () => { live = false; urls.forEach((u) => URL.revokeObjectURL(u)); };
  }, [receiptId, ids]);
  return blobs;
}

// pages: [{ key, name, kind: 'upload' | 'link' | 'new', url, isImage, href, loading }] in reading order.
export function PageStrip({ pages, canEdit, busy, prefix, onAdd, onRemove, onOpen, hint }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const room = MAX_PAGES - pages.length;
  const add = useCallback((list) => {
    const files = acceptedFiles(list);
    if (files.length && canEdit && !busy) onAdd(files.slice(0, Math.max(room, 0)));
  }, [canEdit, busy, onAdd, room]);
  // A paste anywhere while the strip is on screen: every image or PDF on the clipboard becomes a page.
  useEffect(() => {
    if (!canEdit) return undefined;
    const onPaste = (e) => {
      const files = pastedFiles(e.clipboardData, prefix, pages.length);
      if (!files.length) return;
      e.preventDefault();
      add(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [canEdit, prefix, pages.length, add]);
  return (
    <div className="uh-ia-pgs">
      <div className="uh-ia-pgstrip">
        {pages.map((p, i) => (
          <div key={p.key} className="uh-ia-pg">
            {p.kind === 'link'
              ? <a className="uh-ia-pg-img tile" href={p.href} target="_blank" rel="noopener noreferrer" title="Opens in Google Drive"><span className="big">Drive</span></a>
              : (
                <button type="button" className={`uh-ia-pg-img${p.isImage && p.url ? '' : ' tile'}`} onClick={() => onOpen(i)} aria-label={`Open page ${i + 1}`} disabled={!p.url && p.kind === 'upload'}>
                  {p.isImage && p.url && <img src={p.url} alt={`Page ${i + 1}`} />}
                  {!p.isImage && <span className="big">{p.kind === 'new' && !p.name.toLowerCase().endsWith('.pdf') ? 'File' : 'PDF'}</span>}
                  {p.isImage && !p.url && <span className="uh-muted">{p.loading === false ? 'No preview' : 'Loading…'}</span>}
                </button>
              )}
            <div className="uh-ia-pg-n"><span>Page {i + 1}</span>{p.kind === 'new' && <span className="uh-faint">new</span>}</div>
            {canEdit && (
              <button type="button" className="uh-ia-pg-del" aria-label={`Remove page ${i + 1}`} title={`Remove page ${i + 1}`} disabled={busy} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(p, i); }}>
                <Ic source={DeleteIcon} sm />
              </button>
            )}
          </div>
        ))}
        {canEdit && room > 0 && (
          <div className={`uh-ia-pg-add${over ? ' over' : ''}${busy ? ' busy' : ''}`} role="button" tabIndex={0}
            onClick={() => inputRef.current?.click()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
            onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
            onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}>
            <Ic source={UploadIcon} />
            <b>{busy ? 'Uploading…' : pages.length ? 'Add pages' : 'Add the receipt'}</b>
            <span>drop, paste or click</span>
            <input ref={inputRef} type="file" accept=".pdf,image/*" multiple hidden onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
          </div>
        )}
      </div>
      {canEdit && <div className="uh-ia-pg-hint">{hint || 'Drop images or a PDF, paste them, or click to choose files. Several at once is fine; pages are read in this order.'}{room <= 0 && ` A receipt holds at most ${MAX_PAGES} pages.`}</div>}
    </div>
  );
}

// A second dialog on top: one page fitted to the window (image, or PDF inline), with Previous / Next and the arrow keys.
export function PageViewer({ pages, index, name, onClose, onIndex }) {
  const ref = useRef(null);
  const n = pages.length;
  const p = pages[index];
  useEffect(() => { const d = ref.current; if (d && !d.open) d.showModal(); return () => { if (d && d.open) d.close(); }; }, []);
  const go = useCallback((d) => { if (n > 1) onIndex((index + d + n) % n); }, [index, n, onIndex]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); } if (e.key === 'ArrowRight') { e.preventDefault(); go(1); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [go]);
  if (!p) return null;
  const label = n > 1 ? `Page ${index + 1} of ${n}` : 'Page 1';
  return (
    <dialog ref={ref} className="uh-dlg uh-ia-dlg uh-ia-preview" aria-label={`${name}, ${label}`}
      onCancel={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }} onClick={(e) => { e.stopPropagation(); if (e.target === ref.current) onClose(); }}>
      <div className="uh-dlg-h">
        <h2>{name}</h2>
        <span className="uh-ia-pg-lbl">{label}{p.name ? ` · ${p.name}` : ''}</span>
        <span className="uh-sp" />
        {n > 1 && <button type="button" className="uh-btn slim" onClick={() => go(-1)}>‹ Previous</button>}
        {n > 1 && <button type="button" className="uh-btn slim" onClick={() => go(1)}>Next ›</button>}
        {p.kind === 'link'
          ? <a className="uh-btn slim" href={p.href} target="_blank" rel="noopener noreferrer">Open in Google Drive</a>
          : p.url && <a className="uh-btn slim" href={p.url} target="_blank" rel="noopener noreferrer">Open in new tab</a>}
        <button type="button" className="uh-ibtn" aria-label="Close preview" onClick={onClose}><Ic source={XSmallIcon} sm /></button>
      </div>
      <div className="uh-ia-preview-b">
        {p.kind === 'link' ? <div className="uh-ia-preview-note">This page is a Google Drive link.</div>
          : !p.url ? <div className="uh-ia-preview-note">{p.loading === false ? 'This page could not be loaded.' : 'Loading…'}</div>
            : p.isImage ? <img src={p.url} alt={label} /> : <iframe title={label} src={p.url} />}
      </div>
    </dialog>
  );
}

// Stored pages of a saved receipt as strip rows, with their thumbnails.
export function storedRows(files, blobs) {
  return (files || []).map((f) => {
    const b = f.kind === 'upload' ? blobs[f.id] : null;
    const got = b && b !== 'failed' ? b : null;
    return { key: `f${f.id}`, id: f.id, name: f.name, kind: f.kind, href: f.url, url: got ? got.url : null, isImage: f.kind === 'upload' && (got ? got.type.startsWith('image/') : !/\.pdf$/i.test(f.name || '')), loading: b === 'failed' ? false : !got };
  });
}

export function newRows(files, urls) {
  return files.map((f) => ({ key: f.key, name: f.file.name, kind: 'new', url: urls[f.key]?.url || null, isImage: isImageFile(f.file), file: f.file }));
}

let seq = 0;
export const asNew = (files) => files.map((file) => ({ key: `n${Date.now()}-${seq += 1}`, file }));

// The receipt screen's pages: adding uploads at once, removing asks first and takes that page only.
export function ReceiptPages({ r, onChanged }) {
  const { canManage, notify } = useIt();
  const blobs = useStoredPages(r.id, r.files);
  const [open, setOpen] = useState(null);         // index of the page in the viewer
  const [confirm, setConfirm] = useState(null);   // the page about to be removed
  const { busy, error, run } = useAction();
  const pages = storedRows(r.files, blobs);
  const add = (files) => run(async () => {
    const out = await itApi.uploadReceiptPages(r.id, files);
    notify(`${out.added === 1 ? 'Page' : `${out.added} pages`} added to ${r.code}`); onChanged();
  });
  const remove = () => run(async () => {
    const i = pages.findIndex((p) => p.key === confirm.key);
    await itApi.removeReceiptPage(r.id, confirm.id); setConfirm(null); notify(`Removed page ${i + 1} from ${r.code}`); onChanged();
  });
  return (
    <div className="uh-ia-pgs-wrap">
      <PageStrip pages={pages} canEdit={canManage} busy={busy} prefix={r.code} onAdd={add} onRemove={(p) => setConfirm(p)} onOpen={setOpen} />
      <Alert>{error}</Alert>
      {open != null && pages[open] && <PageViewer pages={pages} index={open} name={`Receipt ${r.code}`} onClose={() => setOpen(null)} onIndex={setOpen} />}
      {confirm && (
        <Modal title={`Remove page ${pages.findIndex((p) => p.key === confirm.key) + 1}?`} onClose={() => setConfirm(null)} footer={<>
          <button type="button" className="uh-btn" onClick={() => setConfirm(null)}>Cancel</button>
          <button type="button" className="uh-btn danger" disabled={busy} onClick={remove}>Remove</button>
        </>}>
          <p>Only this page comes off {r.code}{pages.length === 1 ? ', which will then show as missing a receipt' : ''}. {confirm.kind === 'link' ? 'The Drive link is forgotten; the file in Drive is untouched.' : 'The file itself stays in the IT Asset Receipts drive.'}</p>
        </Modal>
      )}
    </div>
  );
}

export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    setBusy(true); setError('');
    try { return await fn(); } catch (e) { setError(e.message); return undefined; } finally { setBusy(false); }
  };
  return { busy, error, setError, run };
}

export { pagesText };
